import { REVIEWS_SECTION_ANCHOR } from "@circle-react/components/SettingsApp/General/ReviewsSettings";
import { useAdminReviewNudge } from "@circle-react/hooks/communityReviews/useAdminReviewNudge";
import { useDismissReviewPrompt } from "@circle-react/hooks/communityReviews/useDismissReviewPrompt";
import { useReviewsTracker } from "@circle-react/hooks/communityReviews/useReviewsTracker";
import { communityReviewInvalidations } from "@circle-react/lib/queries/communityReviews";
import { useUpdateReviewSettingsMutation } from "@circle-react/lib/queries/reviewSettings";
import { settingsRoutes } from "@circle-react/lib/routes/settings";
import { useAdminNudgeModalStore } from "@circle-react/stores/useAdminNudgeModalStore";
import { useReviewSubmissionModalStore } from "@circle-react/stores/useReviewSubmissionModalStore";
import { Button } from "@circleco/compass/components/Button";
import { Drawer } from "@circleco/compass/components/Drawer";
import { Modal } from "@circleco/compass/components/Modal";
import { Typography } from "@circleco/compass/components/Typography";
import { useToast } from "@circleco/compass/context/CompassContext";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

const I18N_ROOT = "settings.reviews.nudge_modal";

const ADMIN_NUDGE_BANNER_URL =
  "https://assets-v2.circle.so/vapwu8aahnela477j3spril6ydb5";

export const AdminReviewNudgeModal = () => {
  const { data: prompt } = useAdminReviewNudge();
  const { mutate: dismissPrompt } = useDismissReviewPrompt();
  const updateReviewSettings = useUpdateReviewSettingsMutation();
  const toast = useToast();
  const tracker = useReviewsTracker();
  const navigate = useNavigate();
  const pathname = useLocation({ select: location => location.pathname });
  const isMobile = useIsMobile();
  const isOpen = useAdminNudgeModalStore(state => state.isOpen);
  const openNudge = useAdminNudgeModalStore(state => state.open);
  const closeNudge = useAdminNudgeModalStore(state => state.close);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [promptAfterDays, setPromptAfterDays] = useState<number | null>(null);
  const hasEvaluated = useRef(false);

  useEffect(() => {
    // Decide once per app session; a later refetch must not reopen it.
    if (!prompt || hasEvaluated.current) return;
    hasEvaluated.current = true;

    // Scoped to the review-invitation modal, not a general "any dialog open" check -- there's no
    // global modal registry yet. Ask #feedback-compass for a real overlay-open signal.
    if (
      !prompt.show_admin_nudge ||
      useReviewSubmissionModalStore.getState().isOpen ||
      pathname === settingsRoutes.reviews()
    )
      return;

    openNudge();
    tracker.trackAdminNudgeViewed();
  }, [prompt, tracker, openNudge, pathname]);

  // One-time nudge: any way the admin exits it without enabling reviews marks it dismissed for good.
  const dismissNudge = () => {
    closeNudge();
    dismissPrompt("admin_nudge");
  };

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    tracker.trackAdminNudgeDismissed();
    dismissNudge();
  };

  const handleLearnMore = () => {
    tracker.trackAdminNudgeLearnMoreClicked();
    dismissNudge();
    void navigate({ to: settingsRoutes.reviews() });
  };

  const handleCollectReviews = () => {
    tracker.trackAdminNudgeCollectReviewsClicked();
    updateReviewSettings.mutate(
      { collect_reviews_enabled: true },
      {
        onSuccess: settings => {
          toast.add({
            title: t([I18N_ROOT, "toast", "success"]),
            variant: "primary",
            icon: "circle-check-filled",
            iconColor: "success",
          });
          closeNudge();
          void communityReviewInvalidations.prompt();
          setPromptAfterDays(settings.prompt_after_days);
          setIsConfirmationOpen(true);
        },
        onError: () => {
          toast.add({
            title: t([I18N_ROOT, "toast", "error"]),
            variant: "destructive",
            icon: "triangle-exclamation-filled",
            iconColor: "danger",
          });
        },
      },
    );
  };

  const handleDone = () => {
    setIsConfirmationOpen(false);
  };

  const handleViewSettings = () => {
    setIsConfirmationOpen(false);
    void navigate({
      to: settingsRoutes.general(),
      hash: REVIEWS_SECTION_ANCHOR,
    });
  };

  const body = (
    <div className="flex flex-col gap-4">
      <img
        src={ADMIN_NUDGE_BANNER_URL}
        alt=""
        aria-hidden="true"
        className="w-full rounded-lg"
      />
      <Typography variant="body-sm" color="primary">
        {t([I18N_ROOT, "body"])}
      </Typography>
    </div>
  );

  const actions = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={handleLearnMore}>
        {t([I18N_ROOT, "learn_more"])}
      </Button>
      <Button
        variant="primary"
        loading={updateReviewSettings.isPending}
        onClick={handleCollectReviews}
      >
        {t([I18N_ROOT, "cta"])}
      </Button>
    </div>
  );

  const confirmationBody = t([I18N_ROOT, "confirmation", "body"], {
    days: promptAfterDays ?? "",
  });

  const confirmation = isMobile ? (
    <Drawer.Root open={isConfirmationOpen} onOpenChange={setIsConfirmationOpen}>
      <Drawer.Content aria-label={t([I18N_ROOT, "confirmation", "title"])}>
        <Drawer.Header
          title={t([I18N_ROOT, "confirmation", "title"])}
          showCloseButton
          closeButtonLabel={t("close")}
        />
        <Typography variant="body-sm" color="primary">
          {confirmationBody}
        </Typography>
        <div className="flex flex-col gap-3 pt-2">
          <Button variant="primary" onClick={handleDone}>
            {t([I18N_ROOT, "confirmation", "done"])}
          </Button>
          <Button variant="secondary" onClick={handleViewSettings}>
            {t([I18N_ROOT, "confirmation", "view_settings"])}
          </Button>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  ) : (
    <Modal.Root
      open={isConfirmationOpen}
      onOpenChange={setIsConfirmationOpen}
      size="md"
    >
      <Modal.Content>
        <Modal.Header title={t([I18N_ROOT, "confirmation", "title"])} />
        <Modal.Body>
          <Typography variant="body-sm" color="primary">
            {confirmationBody}
          </Typography>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t([I18N_ROOT, "confirmation", "view_settings"]),
            onClick: handleViewSettings,
          }}
          primaryAction={{
            label: t([I18N_ROOT, "confirmation", "done"]),
            onClick: handleDone,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );

  if (!isOpen && !isConfirmationOpen) return null;

  if (isConfirmationOpen) return confirmation;

  if (isMobile) {
    return (
      <Drawer.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Drawer.Content aria-label={t([I18N_ROOT, "title"])}>
          <Drawer.Header
            title={t([I18N_ROOT, "title"])}
            showCloseButton
            closeButtonLabel={t("close")}
          />
          {body}
          <div className="flex flex-col gap-3 pt-2">{actions}</div>
        </Drawer.Content>
      </Drawer.Root>
    );
  }

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange} size="md">
      <Modal.Content>
        <Modal.Header title={t([I18N_ROOT, "title"])} />
        <Modal.Body>{body}</Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t([I18N_ROOT, "learn_more"]),
            onClick: handleLearnMore,
            shouldCloseModal: false,
          }}
          primaryAction={{
            label: t([I18N_ROOT, "cta"]),
            onClick: handleCollectReviews,
            loading: updateReviewSettings.isPending,
            shouldCloseModal: false,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
